import { type Dispatch, type SetStateAction, useCallback, useRef, useState } from 'react';
import DepartureIcon from '../../image/icons/Inputs/DepartureIcon.svg';
import { type AutocompleteResponse } from '../../types/Autocomplete';
import DarkCloseIcon from '../../image/icons/Inputs/DarkCloseIcon.svg';
import MaPositionIcon from '../../image/icons/Inputs/MyLocationIcon.png';
import { TripService } from '../../services/TripService';
import FlowRideIcon from '../../image/icons/FlowRideIcon.svg';
import FlowRideIconArchive from '../../image/icons/FlowRideIconArchive.svg';
import { runOnTimeoutIfNotQueued } from '../../tools/DeferredRunTools';
import { deduplicateHistory, filterSearchHistory } from '../../tools/SearchInputs';
import ClickOutsideHandler from '../shared/ClickOutsideHandler';
import { type UnifiedItineraryDTO } from '../../types/Itinerary';
import { useMain } from '../../store/StoredContext';

interface ContainerProps {
  autoCompleteDepartureValues: AutocompleteResponse[];
  setAutocompleteDepartureValues: Dispatch<SetStateAction<AutocompleteResponse[]>>;
  lat: number;
  lon: number;
  departureSelectedAddress: AutocompleteResponse | null;
  setDepartureSelectedAddress: Dispatch<SetStateAction<AutocompleteResponse | null>>;
  input: string;
  setInput: Dispatch<SetStateAction<string>>;
  isMerge: boolean;
  setIsMerge: Dispatch<SetStateAction<boolean>>;
  DepartureInputRef: React.RefObject<HTMLInputElement>;
  arrivalInput: string;
  setSelectedTrip: Dispatch<SetStateAction<UnifiedItineraryDTO | null>>;
  setTripListHeight: Dispatch<SetStateAction<number>>;
  setITakeMyCarData: Dispatch<SetStateAction<UnifiedItineraryDTO | null>>;
}

const tripService = new TripService();

const DepartureInput: React.FC<ContainerProps> = ({
  autoCompleteDepartureValues: autoComplete,
  setAutocompleteDepartureValues,
  lat,
  lon,
  departureSelectedAddress,
  setDepartureSelectedAddress,
  input,
  setInput,
  DepartureInputRef,
  setIsMerge,
  isMerge,
  arrivalInput,
  setSelectedTrip,
  setTripListHeight,
  setITakeMyCarData
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [showAutocomplete, setShowAutocomplete] = useState<boolean>(false);
  const ResultsRef = useRef<HTMLDivElement>(null);
  const [needToCompleteFromGoogle, setNeedToCompleteFromGoogle] = useState<boolean>(true);

  const {
    state: { searchHistory },
    setSearchHistory
  } = useMain();

  // Handle input change and fetch suggestions
  const getSuggestions = useCallback(
    (e: string) => {
      if (e.length === 0) {
        const LatestThree = filterSearchHistory(e, true, searchHistory);
        setAutocompleteDepartureValues([...LatestThree]);
        setShowAutocomplete(true);
        setNeedToCompleteFromGoogle(false);
        return;
      }

      if (e.length <= 3) {
        const findInLocal = filterSearchHistory(e, true, searchHistory);
        setAutocompleteDepartureValues([...findInLocal]);
        setShowAutocomplete(true);
        setNeedToCompleteFromGoogle(false);
        return;
      }

      runOnTimeoutIfNotQueued(
        'autoComplete',
        () => {
          setLoading(true);
          tripService
            .autocomplete({
              query: e,
              lat,
              lon
            })
            .then((res) => {
              const findInLocalAndPelias = filterSearchHistory(e, false, [...searchHistory, ...res.data]);
              setAutocompleteDepartureValues(findInLocalAndPelias);
              setLoading(false);
              setShowAutocomplete(true);
              setNeedToCompleteFromGoogle(true);
            })
            .catch((err) => {
              console.log(err);
              setLoading(false);
              setNeedToCompleteFromGoogle(true);
            });
        },
        500
      );
    },
    [lat, lon, searchHistory, setAutocompleteDepartureValues]
  );

  const handleChanges = useCallback(
    (e: string) => {
      if (e !== input) setInput(e);
      getSuggestions(e === 'mylocation' ? '' : e);
    },
    [getSuggestions, input, setInput]
  );

  const searchGoogle = async () => {
    setLoading(true);
    try {
      const resp = await tripService.autocompleteg({
        query: input,
        lat,
        lon
      });

      setAutocompleteDepartureValues(deduplicateHistory([...autoComplete, ...resp.data]));
      setNeedToCompleteFromGoogle(false);
      if (ResultsRef.current != null) {
        ResultsRef.current.scrollTop = ResultsRef.current.scrollHeight;
      }
      setLoading(false);
    } catch (error) {
      setNeedToCompleteFromGoogle(true);
      setLoading(false);
    }
  };

  const HandleAddress = (item: AutocompleteResponse) => {
    setAutocompleteDepartureValues([]);
    setShowAutocomplete(false);
    if (item.source !== 'mylocation') setSearchHistory(item, true);
    setDepartureSelectedAddress(item);
    setInput(item.address);
    setSelectedTrip(null);
    setITakeMyCarData(null);
    setTripListHeight(50);
    setNeedToCompleteFromGoogle(true);

    if (arrivalInput.length > 0) setIsMerge(true);
  };

  const closeAutoComplete = () => {
    setAutocompleteDepartureValues([]);
  };

  return (
    <div
      className={`absolute top-12 left-3 right-3 ml-auto mr-auto ${isMerge ? 'z-20' : 'z-30'} rounded-2xl transition-top duration-1000 ease shadow-md md:w-3/5`}
    >
      <div className={`bg-white flex items-center p-2 h-12 ${showAutocomplete ? 'rounded-b-none rounded-tl-2xl' : 'rounded-l-2xl rounded-br-2xl'}`}>
        <div
          className="cursor-pointer"
          onClick={() => {
            setInput('');
            setAutocompleteDepartureValues([]);
            setDepartureSelectedAddress(null);
            setIsMerge(true);
          }}
        >
          <img src={DepartureIcon} />
        </div>
        <div className="w-full px-3">
          <div className="flex items-center">
            <div className="w-[95%]">
              <input
                placeholder="Départ"
                type="text"
                value={input}
                ref={DepartureInputRef}
                onChange={(e) => {
                  handleChanges(e.target.value);
                }}
                onFocus={(e) => {
                  handleChanges(e.target.value);
                }}
                className="w-full border-none outline-none bg-transparent text-ionic-brand-blue font-medium"
              />
            </div>
            {(showAutocomplete || input.length > 0) && (
              <span
                className="ml-2 h-full flex items-center justify-center cursor-pointer"
                onClick={() => {
                  setAutocompleteDepartureValues([]);
                  setDepartureSelectedAddress(null);
                  handleChanges('');
                  setShowAutocomplete(false);
                }}
              >
                <img src={DarkCloseIcon} className="w-[16px]" />
              </span>
            )}
          </div>
        </div>
      </div>
      {/* Auto complete response */}
      {showAutocomplete && !loading && (
        <ClickOutsideHandler
          wrapperRef={ResultsRef}
          onOutsideClick={closeAutoComplete}
          className="mp-0 pr-3 pl-3 backdrop-blur-sm bg-white/45 rounded-b-2xl cursor-pointer max-h-96 overflow-y-auto"
        >
          <div
            className="flex p-3 cursor-pointer items-center justify-start text-ionic-brand-blue border-b border-b-ionic-light-gray"
            onClick={() => {
              /** -1 signifie qu'il faut prendre la position de l'utilisateur au moment de la requête, pas maintenant */
              HandleAddress({
                location: {
                  lat: -1,
                  lon: -1
                },
                address: 'Ma position',
                source: 'mylocation'
              });
            }}
          >
            <img src={MaPositionIcon} className="max-w-7 mr-4" />
            <p className="p-0 m-0 font-medium first-letter:capitalize">Ma position</p>
          </div>
          {autoComplete.map((item: AutocompleteResponse, index: number) => {
            return (
              <div
                key={index}
                className="flex p-3 cursor-pointer items-center justify-start text-ionic-brand-blue border-b border-b-ionic-light-gray"
                onClick={() => {
                  HandleAddress(item);
                }}
              >
                {item.source === 'l' ? <img src={FlowRideIconArchive} className="max-w-sm mr-4" /> : <img src={FlowRideIcon} className="max-w-sm mr-4" />}
                <p className="p-0 m-0 font-medium first-letter:capitalize">{item.address}</p>
              </div>
            );
          })}
          {needToCompleteFromGoogle && (
            <h3
              className="pt-5 pb-5 cursor-pointer w-full font-medium capitalize text-ionic-brand-blue text-center"
              onClick={() => {
                void searchGoogle();
              }}
            >
              Recherche approfondie...
            </h3>
          )}
        </ClickOutsideHandler>
      )}
    </div>
  );
};

export default DepartureInput;
